import {
  DollarCircleOutlined,
  KeyOutlined,
  LogoutOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ArrowLeftIcon, ArrowRightIcon, MoonIcon, SunIcon, UserIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Dropdown, MenuProps, Popover, Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/interface';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import styles from './UserInfo.module.css';

import { EmptyBellIconComponent } from '../../../_shared/IconControls';
import { AppThemeContext } from '../../../hooks/use-theme';
import { SignedInUser } from '../../../services/auth';
import { useNotifications } from '../../../side-nav/controls/notifications/notifications-hooks';
import { NotificationsListControl } from '../../../side-nav/controls/notifications/views/NotificationsListControl';
import { useSideNav } from '../../../side-nav/sidenav-hooks';
import routes from '../../../utils/routes';
import { useDocumentation } from '../../documentation/documentation-hooks';
import { getDocumentationNav } from '../../documentation/views/DocumentationNav';
import {
  ABOUT_MENU_ITEM_KEY,
  ADD_GITLAB_API_KEY,
  API_GEN_ITEM_KEY,
  EMAIL_ITEM_KEY,
  LOGOUT_ITEM_KEY,
  SUPPORT_MENU_ITEM_KEY,
  USAGE_KEY,
  USERNAME_ITEM_KEY,
  USER_PREFERENCES_KEY,
} from '../models/user-info';
import { useUserInfo } from '../user-info-hook';
import { AboutDialog } from './AboutDialog';
import { AddGitlabApiKeyDelegate } from './AddGitlabApiKeyDialog';
import { GenerateApiKeyDelegate } from './GenerateApiKeyDialog';
import { UserPreferencesDelegate } from './UserPreferencesDialog';

export interface UserInfoDelegate extends GenerateApiKeyDelegate, AddGitlabApiKeyDelegate, UserPreferencesDelegate {}

export function UserInfo({ user, signOut }: { user: SignedInUser; signOut: () => void }) {
  const { theme, toggleTheme } = useContext(AppThemeContext);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { documentationModel } = useDocumentation();
  const { notificationsModel, notificationsDelegate } = useNotifications();
  const { sideNavModel, sideNavDelegate } = useSideNav();
  const { userInfoModel: model, userInfoDelegate: delegate } = useUserInfo();

  const { collapsed: sidebarCollapsed } = sideNavModel;
  const { isMidasPeer } = window.CONFIG;

  const handleSidebarCollapseToggle = () => {
    sideNavDelegate.setCollapsed(!sidebarCollapsed);
  };

  const handleAboutDialogClose = useCallback(() => {
    setAboutDialogOpen(false);
  }, [setAboutDialogOpen]);

  const createSupportMail = useCallback(() => {
    const {
      supportEmailAddress,
      branding: { shortName },
    } = window.CONFIG;
    window.open(`mailto:${supportEmailAddress}?subject=${shortName}%20Support%20Question`);
  }, []);

  const handleDropDownMenuItemClick: MenuProps['onClick'] = (menuInfo) => {
    switch (menuInfo.key) {
      case LOGOUT_ITEM_KEY:
        signOut();
        break;
      case API_GEN_ITEM_KEY:
        delegate.onStartGenerateApiKey();
        break;
      case ADD_GITLAB_API_KEY:
        void delegate.onStartAddGitlabApiKey();
        break;
      case USER_PREFERENCES_KEY:
        void delegate.onOpenUserPreferences();
        break;
      case USAGE_KEY:
        void navigate(routes.Usage);
        break;
      case SUPPORT_MENU_ITEM_KEY:
        createSupportMail();
        break;
      case ABOUT_MENU_ITEM_KEY:
        setAboutDialogOpen(true);
        break;
      default:
        break;
    }

    if (menuInfo.key.startsWith(routes.Documentation)) {
      void navigate(menuInfo.key);
    }
  };

  const menuItems: ItemType[] = useMemo(
    () => [
      {
        key: USERNAME_ITEM_KEY,
        icon: <UserOutlined />,
        label: `${user?.attributes?.name ?? 'No Name'}`,
        disabled: true,
      },
      {
        key: EMAIL_ITEM_KEY,
        icon: <MailOutlined />,
        label: `${user?.attributes?.email ?? 'No Email'}`,
        disabled: true,
      },
      {
        type: 'divider',
      },
      {
        key: API_GEN_ITEM_KEY,
        icon: <KeyOutlined />,
        label: 'Generate API Key...',
      },
      isMidasPeer
        ? {
            key: ADD_GITLAB_API_KEY,
            icon: <KeyOutlined />,
            label: 'GitLab API Key...',
          }
        : null,
      {
        type: 'divider',
      },
      model.workbenchAllowed
        ? {
            key: USER_PREFERENCES_KEY,
            icon: <SettingOutlined />,
            label: 'Preferences',
          }
        : null,
      model.workbenchAllowed
        ? {
            key: USAGE_KEY,
            icon: <DollarCircleOutlined />,
            label: 'Usage',
          }
        : null,
      model.workbenchAllowed
        ? {
            type: 'divider',
          }
        : null,
      {
        key: SUPPORT_MENU_ITEM_KEY,
        icon: <MailOutlined />,
        label: 'Create Support Email...',
      },
      {
        type: 'divider',
      },
      {
        key: ABOUT_MENU_ITEM_KEY,
        icon: <QuestionCircleOutlined />,
        label: 'About',
      },
      {
        type: 'divider',
      },
      {
        key: LOGOUT_ITEM_KEY,
        icon: <LogoutOutlined />,
        label: 'Log out',
      },
    ],
    [user?.attributes?.email, user?.attributes?.name, isMidasPeer, model.workbenchAllowed]
  );

  const unseenNotifications = notificationsModel.notifications.filter((n) => !n.isSeen).length;

  const notificationsPopover = (
    <Popover
      trigger="click"
      content={<NotificationsListControl />}
      onOpenChange={(open) => {
        if (!open) {
          void notificationsDelegate?.onSetNotificationsSeen();
        }
      }}
    >
      <Badge count={unseenNotifications > 99 ? '99+' : unseenNotifications}>
        <Button aria-label="Notifications" type="text" icon={<EmptyBellIconComponent />} />
      </Badge>
    </Popover>
  );
  return (
    <>
      <div className={`${styles.sidebarButtonsContainer} ${sidebarCollapsed ? styles.contractedContainer : styles.expandedContainer}`}>
        {!sidebarCollapsed && (
          <>
            <Tooltip title="Toggle theme">
              <Button
                type="text"
                onClick={toggleTheme}
                icon={theme === 'light' ? <MoonIcon height={16} width={16} /> : <SunIcon height={18} width={18} />}
                aria-label="Toggle theme"
              />
            </Tooltip>

            <Dropdown
              menu={{ items: getDocumentationNav({ model: documentationModel }).children, onClick: handleDropDownMenuItemClick }}
              placement="bottomLeft"
              trigger={['click']}
            >
              <Tooltip title="Documentation">
                <Button type="text" icon={<QuestionCircleOutlined height={18} width={18} />} aria-label="Open documentation dropdown" />
              </Tooltip>
            </Dropdown>

            <Dropdown menu={{ items: menuItems, onClick: handleDropDownMenuItemClick }} placement="bottomLeft" trigger={['click']}>
              <Tooltip title="User Account">
                <Button type="text" icon={<UserIcon height={18} width={18} />} aria-label="Open user account dropdown" />
              </Tooltip>
            </Dropdown>

            {notificationsPopover}
          </>
        )}
        {sidebarCollapsed && notificationsPopover}
        <Button
          type="text"
          onClick={handleSidebarCollapseToggle}
          icon={sidebarCollapsed ? <ArrowRightIcon height={18} width={18} /> : <ArrowLeftIcon height={18} width={18} />}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
      </div>
      <AboutDialog isOpen={aboutDialogOpen} onClose={handleAboutDialogClose} />
    </>
  );
}
